/**
 * NavigatorX Real-Time Analytics Dashboard
 * 
 * Enterprise-grade analytics visualization:
 * - Real-time user activity monitoring
 * - DAU/WAU/MAU metrics with charts
 * - Feature usage breakdown
 * - Retention analysis
 * - Funnel visualization
 * - Export capabilities
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Activity,
  Users,
  TrendingUp,
  TrendingDown,
  BarChart3,
  LineChart,
  PieChart,
  Download,
  RefreshCw,
  Loader2,
  Eye,
  Navigation,
  Search,
  Star,
  AlertTriangle,
  Mic,
  Clock,
  Calendar,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { Timestamp } from 'firebase/firestore';
import {
  getAnalyticsSummary,
  getUsageMetrics,
  getFeatureUsageStats,
  getSignupFunnel,
  getNavigationFunnel,
  getRealtimeStats,
  getDauHistory,
  getRetentionData,
  getScreenAnalytics,
  getSearchAnalytics,
  exportToCsv,
  type UsageMetrics,
  type FeatureUsage,
  type FunnelStep,
  type RealtimeStats,
  type ScreenAnalytics,
  type SearchAnalytics,
  type RetentionData
} from '../../../utils/services/navigatorXAnalyticsService';

interface RealTimeAnalyticsDashboardProps {
  embedded?: boolean;
}

type ActiveTab = 'overview' | 'users' | 'features' | 'funnels' | 'retention' | 'realtime';

export default function RealTimeAnalyticsDashboard({ embedded = false }: RealTimeAnalyticsDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  
  // Data state
  const [usage, setUsage] = useState<UsageMetrics | null>(null);
  const [features, setFeatures] = useState<FeatureUsage[]>([]);
  const [signupFunnel, setSignupFunnel] = useState<FunnelStep[]>([]);
  const [navFunnel, setNavFunnel] = useState<FunnelStep[]>([]);
  const [realtime, setRealtime] = useState<RealtimeStats | null>(null);
  const [dauHistory, setDauHistory] = useState<{ date: string; count: number }[]>([]);
  const [retention, setRetention] = useState<RetentionData | null>(null);
  const [screens, setScreens] = useState<ScreenAnalytics[]>([]);
  const [searches, setSearches] = useState<SearchAnalytics[]>([]);
  const [realtimeStale, setRealtimeStale] = useState(false);
  
  // Auto-refresh for realtime tab
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (activeTab === 'realtime') {
      interval = setInterval(() => {
        loadRealtimeData();
      }, 30000); // Refresh every 30 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab]);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const summary = await getAnalyticsSummary();
      setUsage(summary.usage);
      setFeatures(summary.features);
      setSignupFunnel(summary.funnel);
      setRealtime(summary.realtime);
      setDauHistory(summary.dauHistory);
      
      // Load additional data
      const [nav, ret, scr, srch] = await Promise.all([
        getNavigationFunnel(30),
        getRetentionData(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], 7),
        getScreenAnalytics(7),
        getSearchAnalytics(7, 20)
      ]);
      
      setNavFunnel(nav);
      setRetention(ret);
      setScreens(scr);
      setSearches(srch);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadRealtimeData = async () => {
    try {
      const rt = await getRealtimeStats();
      setRealtime(rt);
      setRealtimeStale(false);
    } catch (error) {
      console.error('Error loading realtime:', error);
      if (!realtimeStale) {
        toast.error('Real-time data may be stale');
      }
      setRealtimeStale(true);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadAllData();
      toast.success('Data refreshed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = (data: any[], filename: string) => {
    const csv = exportToCsv(data, filename);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export downloaded');
  };

  const getFeatureIcon = (feature: string) => {
    switch (feature) {
      case 'navigation': return Navigation;
      case 'search': return Search;
      case 'favorites': return Star;
      case 'hazards': return AlertTriangle;
      case 'voice': return Mic;
      default: return Activity;
    }
  };

  const formatNumber = (n: number): string => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
            <p className="text-gray-500">Real-time insights into NavigatorX usage</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={() => handleExport(features, 'feature_usage')}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Daily Active</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(usage?.dau || 0)}</p>
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <ArrowUpRight className="h-3 w-3 mr-1" />
                  vs yesterday
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Weekly Active</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(usage?.wau || 0)}</p>
                <p className="text-xs text-gray-500">Last 7 days</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Calendar className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Monthly Active</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(usage?.mau || 0)}</p>
                <p className="text-xs text-gray-500">Last 30 days</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Stickiness</p>
                <p className="text-2xl font-bold text-gray-900">{usage?.dauWauRatio || 0}%</p>
                <p className="text-xs text-gray-500">DAU/WAU ratio</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Zap className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'users', label: 'Users', icon: Users },
          { id: 'features', label: 'Features', icon: Activity },
          { id: 'funnels', label: 'Funnels', icon: TrendingDown },
          { id: 'retention', label: 'Retention', icon: TrendingUp },
          { id: 'realtime', label: 'Real-time', icon: Zap }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as ActiveTab)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
            {tab.id === 'realtime' && realtime && (
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid gap-6 md:grid-cols-2">
          {/* DAU Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Daily Active Users (14 days)</CardTitle>
            </CardHeader>
            <CardContent>
              {dauHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-12">No DAU data</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={dauHistory} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                    <defs>
                      <linearGradient id="dauGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" allowDecimals={false} />
                    <Tooltip
                      formatter={(value: number) => [value, 'Active Users']}
                      labelStyle={{ fontWeight: 600 }}
                      contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#6366f1"
                      strokeWidth={2}
                      fill="url(#dauGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Feature Usage */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Feature Usage (7 days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {features.slice(0, 5).map(feature => {
                  const Icon = getFeatureIcon(feature.feature);
                  return (
                    <div key={feature.feature} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                        <Icon className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="capitalize">{feature.feature}</span>
                          <span className="text-gray-500">{feature.percentOfUsers}%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ width: `${feature.percentOfUsers}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Signup Funnel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Signup Funnel (30 days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {signupFunnel.map((step, i) => (
                  <div key={step.name} className="flex items-center gap-3">
                    <div className="w-6 text-center text-sm font-medium text-gray-400">{i + 1}</div>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>{step.name}</span>
                        <span className="font-medium">{formatNumber(step.count)}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            step.conversionRate >= 50 ? 'bg-green-500' :
                            step.conversionRate >= 25 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${step.conversionRate}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-16 text-right">
                      <Badge variant="outline" className={
                        step.dropOffRate > 50 ? 'text-red-600' : ''
                      }>
                        {i === 0 ? '100%' : `${step.conversionRate}%`}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Screen Analytics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Screen Views (7 days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {screens.slice(0, 5).map(screen => (
                  <div key={screen.screenName} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm">{screen.screenName.replace('Screen', '')}</span>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-500">{formatNumber(screen.views)} views</span>
                      <span className="text-gray-500">{screen.avgTimeOnScreen}s avg</span>
                      <Badge variant="outline" className={
                        screen.bounceRate > 50 ? 'text-red-600' : 'text-green-600'
                      }>
                        {screen.bounceRate}% bounce
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>User Engagement Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Avg Sessions/User</p>
                  <p className="text-2xl font-bold">{usage?.avgSessionsPerUser.toFixed(1) || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Avg Session Duration</p>
                  <p className="text-2xl font-bold">{usage?.avgSessionDuration || 0} min</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Sessions</p>
                  <p className="text-2xl font-bold">{formatNumber(usage?.totalSessions || 0)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">DAU/WAU Ratio</p>
                  <p className="text-2xl font-bold">{usage?.dauWauRatio || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Search Analytics */}
          <Card>
            <CardHeader>
              <CardTitle>Top Searches (7 days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {searches.slice(0, 10).map((search, i) => (
                  <div key={search.term} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium">
                        {i + 1}
                      </span>
                      <span className="text-sm font-medium">{search.term}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-500">{search.count} searches</span>
                      <Badge variant="outline">
                        {search.clickThroughRate}% CTR
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Features Tab */}
      {activeTab === 'features' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Feature Usage Details</CardTitle>
              <Button variant="outline" size="sm" onClick={() => handleExport(features, 'feature_usage')}>
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {features.map(feature => {
                const Icon = getFeatureIcon(feature.feature);
                return (
                  <div key={feature.feature} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <Icon className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="font-medium capitalize">{feature.feature}</h4>
                          <p className="text-sm text-gray-500">{feature.uniqueUsers} unique users</p>
                        </div>
                      </div>
                      <Badge className={feature.percentOfUsers >= 50 ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
                        {feature.percentOfUsers}% adoption
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Total Usage</p>
                        <p className="font-medium">{formatNumber(feature.usageCount)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Avg per User</p>
                        <p className="font-medium">{feature.avgUsagePerUser}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">User Adoption</p>
                        <div className="w-full h-2 bg-gray-200 rounded-full mt-1">
                          <div
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ width: `${feature.percentOfUsers}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Funnels Tab */}
      {activeTab === 'funnels' && (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Signup Funnel */}
          <Card>
            <CardHeader>
              <CardTitle>Signup Funnel</CardTitle>
              <CardDescription>User acquisition flow (30 days)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {signupFunnel.map((step, i) => {
                  const maxCount = signupFunnel[0]?.count || 1;
                  const width = (step.count / maxCount) * 100;
                  return (
                    <div key={step.name}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{step.name}</span>
                        <span className="font-medium">{formatNumber(step.count)}</span>
                      </div>
                      <div className="relative">
                        <div className="w-full h-8 bg-gray-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 flex items-center justify-end pr-2"
                            style={{ width: `${width}%` }}
                          >
                            {width > 20 && (
                              <span className="text-xs text-white font-medium">
                                {step.conversionRate}%
                              </span>
                            )}
                          </div>
                        </div>
                        {i > 0 && step.dropOffRate > 0 && (
                          <div className="absolute -right-2 top-1/2 -translate-y-1/2 text-xs text-red-500">
                            -{step.dropOffRate}%
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Navigation Funnel */}
          <Card>
            <CardHeader>
              <CardTitle>Navigation Funnel</CardTitle>
              <CardDescription>Core feature usage flow (30 days)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {navFunnel.map((step, i) => {
                  const maxCount = navFunnel[0]?.count || 1;
                  const width = (step.count / maxCount) * 100;
                  return (
                    <div key={step.name}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{step.name}</span>
                        <span className="font-medium">{formatNumber(step.count)}</span>
                      </div>
                      <div className="relative">
                        <div className="w-full h-8 bg-gray-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-green-500 to-green-400 flex items-center justify-end pr-2"
                            style={{ width: `${width}%` }}
                          >
                            {width > 20 && (
                              <span className="text-xs text-white font-medium">
                                {step.conversionRate}%
                              </span>
                            )}
                          </div>
                        </div>
                        {i > 0 && step.dropOffRate > 0 && (
                          <div className="absolute -right-2 top-1/2 -translate-y-1/2 text-xs text-red-500">
                            -{step.dropOffRate}%
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Retention Tab */}
      {activeTab === 'retention' && !retention && (
        <Card>
          <CardContent className="p-8">
            <div className="text-center py-8">
              <TrendingUp className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">No retention data available</p>
              <p className="text-sm text-gray-400 mt-1">Retention data will appear once users have been active for multiple days</p>
            </div>
          </CardContent>
        </Card>
      )}
      {activeTab === 'retention' && retention && (
        <Card>
          <CardHeader>
            <CardTitle>Retention Analysis</CardTitle>
            <CardDescription>
              Cohort from {retention.cohortDate} ({retention.cohortSize} users)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {retention.retentionByDay.map(day => (
                <div key={day.day} className="flex items-center gap-4">
                  <div className="w-16 text-sm text-gray-500">Day {day.day}</div>
                  <div className="flex-1">
                    <div className="w-full h-6 bg-gray-100 rounded overflow-hidden">
                      <div
                        className={`h-full rounded flex items-center pl-2 ${
                          day.percentage >= 50 ? 'bg-green-500' :
                          day.percentage >= 25 ? 'bg-yellow-500' :
                          day.percentage >= 10 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.max(day.percentage, 5)}%` }}
                      >
                        {day.percentage >= 15 && (
                          <span className="text-xs text-white font-medium">{day.percentage}%</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-24 text-right">
                    <span className="text-sm font-medium">{day.retained}</span>
                    <span className="text-sm text-gray-500"> / {retention.cohortSize}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-2">Retention Benchmarks</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Day 1</p>
                  <p className="font-medium text-lg">
                    {retention.retentionByDay.find(d => d.day === 1)?.percentage || 0}%
                  </p>
                  <p className="text-xs text-gray-400">Target: 40%+</p>
                </div>
                <div>
                  <p className="text-gray-500">Day 7</p>
                  <p className="font-medium text-lg">
                    {retention.retentionByDay.find(d => d.day === 7)?.percentage || 0}%
                  </p>
                  <p className="text-xs text-gray-400">Target: 20%+</p>
                </div>
                <div>
                  <p className="text-gray-500">Overall</p>
                  <p className="font-medium text-lg">
                    {Math.round(
                      retention.retentionByDay.reduce((sum, d) => sum + d.percentage, 0) / 
                      retention.retentionByDay.length
                    )}%
                  </p>
                  <p className="text-xs text-gray-400">Avg retention</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Real-time Tab */}
      {activeTab === 'realtime' && !realtime && (
        <Card>
          <CardContent className="p-8">
            <div className="text-center py-8">
              <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">No real-time data available</p>
              <p className="text-sm text-gray-400 mt-1">Real-time activity will appear when users are active in the app</p>
            </div>
          </CardContent>
        </Card>
      )}
      {activeTab === 'realtime' && realtime && (
        <div className="grid gap-6">
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Active Now</p>
                    <p className="text-3xl font-bold text-green-600">{realtime.activeUsers}</p>
                  </div>
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div>
                  <p className="text-xs text-gray-500">Active Navigations</p>
                  <p className="text-3xl font-bold text-gray-900">{realtime.activeNavigations}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div>
                  <p className="text-xs text-gray-500">Last Updated</p>
                  <p className="text-lg font-medium text-gray-700">
                    {realtime.updatedAt?.toDate?.()?.toLocaleTimeString() || 'Just now'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Live Event Feed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {realtime.recentEvents.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No recent events</p>
                ) : (
                  realtime.recentEvents.map(event => (
                    <div key={event.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{event.eventType}</Badge>
                          <code className="text-xs text-gray-400">{event.userId.substring(0, 8)}...</code>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {event.timestamp?.toDate?.()?.toLocaleTimeString()}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
